import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", phone: "", speciality: "", is_active: true };

export default function AdminRecruiters() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/recruiters").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/recruiters/${data.id}`, data);
      else await api.post("/admin/recruiters", data);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this recruiter?")) return;
    await api.delete(`/admin/recruiters/${id}`); toast.success("Removed"); load();
  };

  return (
    <div className="space-y-6" data-testid="admin-recruiters">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Recruiters</h1>
          <p className="text-sm text-slate-500">Team members with active pipelines</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} data-testid="recruiter-new"><Plus className="mr-1 h-4 w-4" />Add recruiter</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`recruiter-${r.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary"><UserCog className="h-5 w-5" /></div>
                <div>
                  <div className="font-heading text-base font-semibold text-slate-900">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.email || "—"} · {r.phone || "—"}</div>
                </div>
              </div>
              <Badge className={r.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>{r.is_active ? "Active" : "Inactive"}</Badge>
            </div>
            {r.speciality && <div className="mt-3 text-sm text-slate-600">{r.speciality}</div>}
            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
              <div className="text-sm">
                <span className="font-heading text-2xl font-bold text-slate-900">{r.active_pipeline || 0}</span>
                <span className="ml-1 text-xs text-slate-500">active candidates</span>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditing(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(r.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-400">No recruiters yet.</div>}
      </div>

      {editing && (
        <Dialog open={true} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent data-testid="recruiter-dialog">
            <DialogHeader><DialogTitle>{editing.id ? "Edit recruiter" : "Add recruiter"}</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <Input placeholder="Full name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="recruiter-name" />
              <Input placeholder="Email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              <Input placeholder="Phone" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
              <Input placeholder="Speciality (e.g. R&D, Formulation, Regulatory)" value={editing.speciality} onChange={(e) => setEditing({ ...editing, speciality: e.target.value })} />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={() => save(editing)} data-testid="recruiter-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
